import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ApiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import QRCode from 'qrcode';
import {
  User as UserIcon,
  Mail,
  Shield,
  Key,
  Lock,
  CheckCircle,
  AlertCircle,
  Copy,
  Check,
  Smartphone,
  X,
  RefreshCw,
  Eye,
  EyeOff
} from 'lucide-react';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose }) => {
  const { user, refreshUser } = useAuth();

  // Tabs: 'profile' | 'password' | '2fa'
  const [activeTab, setActiveTab] = useState<'profile' | 'password' | '2fa'>('profile');

  // Profile fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password change fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // 2FA fields
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [setupSecret, setSetupSecret] = useState('');
  const [setupUri, setSetupUri] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [copiedKey, setCopiedKey] = useState(false);
  const [twoFaLoading, setTwoFaLoading] = useState(false);
  const [twoFaMsg, setTwoFaMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadProfile();
    }
  }, [isOpen]);

  // Close on Escape key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const loadProfile = async () => {
    const res = await ApiClient.getProfile();
    if (res.ok && res.user) {
      setName(res.user.name || '');
      setEmail(res.user.email || '');
      setNotificationEmail(res.user.notification_email || '');
      setTotpEnabled(Boolean(res.user.totp_enabled));
    } else if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
      setNotificationEmail(user.notification_email || '');
      setTotpEnabled(Boolean(user.totp_enabled));
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMsg(null);

    const res = await ApiClient.updateProfile({
      name: name.trim(),
      email: email.trim(),
      notification_email: notificationEmail.trim() || undefined,
    });

    if (res.ok) {
      setProfileMsg({ type: 'success', text: 'Profil adatai sikeresen elmentve!' });
      await refreshUser();
    } else {
      setProfileMsg({ type: 'error', text: res.error || 'Hiba a mentés során' });
    }
    setProfileSaving(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMsg(null);

    if (newPassword.length < 8) {
      setPasswordMsg({ type: 'error', text: 'Az új jelszónak legalább 8 karakter hosszúnak kell lennie!' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordMsg({ type: 'error', text: 'Az új jelszavak nem egyeznek!' });
      return;
    }

    setPasswordSaving(true);
    const res = await ApiClient.changeOwnPassword(currentPassword, newPassword);
    if (res.ok) {
      setPasswordMsg({ type: 'success', text: 'A jelszó sikeresen megváltoztatva!' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } else {
      setPasswordMsg({ type: 'error', text: res.error || 'A jelenlegi jelszó helytelen vagy hiba történt' });
    }
    setPasswordSaving(false);
  };

  const handleStart2FASetup = async () => {
    setTwoFaLoading(true);
    setTwoFaMsg(null);
    const res = await ApiClient.setup2FA();
    if (res.ok && res.secret && res.uri) {
      setSetupSecret(res.secret);
      setSetupUri(res.uri);
      try {
        const dataUrl = await QRCode.toDataURL(res.uri, {
          width: 200,
          margin: 1.5,
          color: {
            dark: '#1e1b4b',
            light: '#ffffff'
          }
        });
        setQrCodeUrl(dataUrl);
      } catch (err) {
        console.error('QR code generation failed', err);
      }
    } else {
      setTwoFaMsg({ type: 'error', text: res.error || 'Nem sikerült legenerálni a 2FA kulcsot' });
    }
    setTwoFaLoading(false);
  };

  const handleVerifyAndEnable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verificationCode || verificationCode.length !== 6) {
      setTwoFaMsg({ type: 'error', text: 'Kérjük, adja meg a 6 jegyű ellenőrző kódot!' });
      return;
    }

    setTwoFaLoading(true);
    setTwoFaMsg(null);
    const res = await ApiClient.enable2FA(setupSecret, verificationCode);
    if (res.ok) {
      setTotpEnabled(true);
      setSetupSecret('');
      setSetupUri('');
      setQrCodeUrl('');
      setVerificationCode('');
      setTwoFaMsg({ type: 'success', text: 'A kétlépcsős azonosítás (2FA) sikeresen aktiválva!' });
      await refreshUser();
    } else {
      setTwoFaMsg({ type: 'error', text: res.error || 'Érvénytelen kód. Kérjük próbálja újra!' });
    }
    setTwoFaLoading(false);
  };

  const handleDisable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!disablePassword) {
      setTwoFaMsg({ type: 'error', text: 'Kérjük, adja meg a jelszavát a 2FA kikapcsolásához!' });
      return;
    }

    setTwoFaLoading(true);
    setTwoFaMsg(null);
    const res = await ApiClient.disable2FA(disablePassword);
    if (res.ok) {
      setTotpEnabled(false);
      setShowDisableConfirm(false);
      setDisablePassword('');
      setTwoFaMsg({ type: 'success', text: 'A kétlépcsős azonosítás kikapcsolva.' });
      await refreshUser();
    } else {
      setTwoFaMsg({ type: 'error', text: res.error || 'Helytelen jelszó' });
    }
    setTwoFaLoading(false);
  };

  const copySecretToClipboard = () => {
    navigator.clipboard.writeText(setupSecret);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      data-esc-self
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col my-auto max-h-[90vh] animate-in zoom-in-95 duration-150"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/50 dark:bg-slate-950/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold">
              <UserIcon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-white">Felhasználói Profil & Biztonság</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">Saját adatok, e-mail címek, jelszó és 2FA kezelése</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 px-6 bg-slate-50/30 dark:bg-slate-900/40">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-3 px-4 text-xs font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === 'profile'
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-slate-300'
            }`}
          >
            <UserIcon className="w-4 h-4" />
            <span>Személyes Adatok</span>
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`py-3 px-4 text-xs font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === 'password'
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-slate-300'
            }`}
          >
            <Key className="w-4 h-4" />
            <span>Jelszóváltoztatás</span>
          </button>
          <button
            onClick={() => setActiveTab('2fa')}
            className={`py-3 px-4 text-xs font-semibold border-b-2 flex items-center gap-2 transition ${
              activeTab === '2fa'
                ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400'
                : 'border-transparent text-slate-500 hover:text-slate-900 dark:hover:text-slate-300'
            }`}
          >
            <Shield className="w-4 h-4" />
            <span>2FA Hitelesítés</span>
            {totpEnabled && (
              <span className="w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-emerald-500/20"></span>
            )}
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* TAB 1: PROFILE */}
          {activeTab === 'profile' && (
            <form onSubmit={handleSaveProfile} className="space-y-4">
              {profileMsg && (
                <div
                  className={`p-3 rounded-xl text-xs flex items-center gap-2 ${
                    profileMsg.type === 'success'
                      ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                      : 'bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300'
                  }`}
                >
                  {profileMsg.type === 'success' ? (
                    <CheckCircle className="w-4 h-4 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 shrink-0" />
                  )}
                  <span>{profileMsg.text}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Teljes Név *
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Minta Péter"
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Elsődleges E-mail Cím (Belépéshez) *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="peter@onkormanyzat.hu"
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  Ezzel az e-mail címmel jelentkezik be a felületre.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Értesítési E-mail Cím (Másodlagos / Jelszó-visszaállítás)
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                    placeholder="peter.ertesites@privat.hu (opcionális)"
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                  Rendszerértesítések, audit figyelmeztetések vagy jelszó-visszaállító hivatkozások másolata ide is küldhető.
                </p>
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                <button
                  type="submit"
                  disabled={profileSaving}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition shadow-sm cursor-pointer disabled:opacity-50 flex items-center gap-2"
                >
                  {profileSaving && <RefreshCw className="w-4 h-4 animate-spin" />}
                  <span>Módosítások Mentése</span>
                </button>
              </div>
            </form>
          )}

          {/* TAB 2: PASSWORD */}
          {activeTab === 'password' && (
            <form onSubmit={handleChangePassword} className="space-y-4">
              {passwordMsg && (
                <div
                  className={`p-3 rounded-xl text-xs flex items-center gap-2 ${
                    passwordMsg.type === 'success'
                      ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                      : 'bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300'
                  }`}
                >
                  {passwordMsg.type === 'success' ? (
                    <CheckCircle className="w-4 h-4 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 shrink-0" />
                  )}
                  <span>{passwordMsg.text}</span>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Jelenlegi Jelszó *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showCurrentPass ? 'text' : 'password'}
                    required
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full pl-9 pr-10 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPass(!showCurrentPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Új Jelszó (min. 8 karakter) *
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showNewPass ? 'text' : 'password'}
                    required
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Legalább 8 karakter..."
                    className="w-full pl-9 pr-10 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPass(!showNewPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  Új Jelszó Ismétlése *
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type={showNewPass ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Ismételje meg az új jelszót..."
                    className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-slate-800 flex justify-end">
                <button
                  type="submit"
                  disabled={passwordSaving}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium transition shadow-sm cursor-pointer disabled:opacity-50 flex items-center gap-2"
                >
                  {passwordSaving && <RefreshCw className="w-4 h-4 animate-spin" />}
                  <span>Jelszó Módosítása</span>
                </button>
              </div>
            </form>
          )}

          {/* TAB 3: 2FA */}
          {activeTab === '2fa' && (
            <div className="space-y-5">
              {twoFaMsg && (
                <div
                  className={`p-3 rounded-xl text-xs flex items-center gap-2 ${
                    twoFaMsg.type === 'success'
                      ? 'bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
                      : 'bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300'
                  }`}
                >
                  {twoFaMsg.type === 'success' ? (
                    <CheckCircle className="w-4 h-4 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 shrink-0" />
                  )}
                  <span>{twoFaMsg.text}</span>
                </div>
              )}

              {/* Status Banner */}
              <div
                className={`p-4 rounded-2xl border flex items-start gap-3.5 ${
                  totpEnabled
                    ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/60'
                    : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200 dark:border-slate-800'
                }`}
              >
                <div
                  className={`p-2.5 rounded-xl shrink-0 ${
                    totpEnabled
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }`}
                >
                  <Smartphone className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                      Kétlépcsős Azonosítás (TOTP 2FA)
                    </h3>
                    {totpEnabled ? (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
                        AKTÍV ÉS VÉDETT
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
                        KIKAPCSOLVA
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                    {totpEnabled
                      ? 'Fiókja védve van: a jelszó megadása után egy 6-jegyű kódot kell beírnia a telefonos Authenticator appból.'
                      : 'Növelje fiókja biztonságát Google Authenticator, Microsoft Authenticator vagy egyéb TOTP alkalmazás használatával.'}
                  </p>
                </div>
              </div>

              {/* Action when disabled: Start Setup */}
              {!totpEnabled && !setupSecret && (
                <div className="text-center py-4">
                  <button
                    onClick={handleStart2FASetup}
                    disabled={twoFaLoading}
                    className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-sm font-medium shadow-md transition cursor-pointer flex items-center gap-2 mx-auto disabled:opacity-50"
                  >
                    {twoFaLoading ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Shield className="w-4 h-4" />
                    )}
                    <span>2FA Beállításának Indítása</span>
                  </button>
                </div>
              )}

              {/* Wizard when setting up */}
              {!totpEnabled && setupSecret && (
                <div className="space-y-4 pt-2 border-t border-slate-200 dark:border-slate-800">
                  <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px]">
                      1
                    </span>
                    <span>Olvassa be a QR-kódot az Authenticator alkalmazásával</span>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-6 p-4 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800">
                    {qrCodeUrl ? (
                      <div className="p-2 bg-white rounded-xl shadow-xs border border-slate-200 shrink-0">
                        <img src={qrCodeUrl} alt="2FA QR Kód" className="w-36 h-36" />
                      </div>
                    ) : (
                      <div className="w-36 h-36 flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-xl">
                        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    )}

                    <div className="flex-1 space-y-2 text-center sm:text-left">
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        Vagy adja meg a titkos kulcsot kézzel az alkalmazásban:
                      </div>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <code className="px-3 py-1.5 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 tracking-wider select-all">
                          {setupSecret}
                        </code>
                        <button
                          type="button"
                          onClick={copySecretToClipboard}
                          className="p-1.5 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg transition"
                          title="Kulcs másolása"
                        >
                          {copiedKey ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                        </button>
                      </div>
                      {setupUri && (
                        <div className="pt-1">
                          <a
                            href={setupUri}
                            className="inline-flex items-center gap-1 text-[11px] text-indigo-600 dark:text-indigo-400 hover:underline"
                          >
                            Megnyitás közvetlenül Authenticator appban
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <form onSubmit={handleVerifyAndEnable2FA} className="space-y-3 pt-2">
                    <div className="text-xs font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <span className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[10px]">
                        2
                      </span>
                      <span>Adja meg az appban megjelenő 6 jegyű kódot a megerősítéshez</span>
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        maxLength={6}
                        required
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="123456"
                        className="w-36 text-center tracking-widest text-lg font-mono font-bold py-2 bg-slate-50 dark:bg-slate-950 border border-slate-300 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      />
                      <button
                        type="submit"
                        disabled={twoFaLoading || verificationCode.length !== 6}
                        className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-semibold shadow-sm transition disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                      >
                        {twoFaLoading && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                        <span>Aktiválás & Bekapcsolás</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSetupSecret('');
                          setQrCodeUrl('');
                        }}
                        className="px-3 py-2 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                      >
                        Mégse
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Action when enabled: Disable 2FA */}
              {totpEnabled && !showDisableConfirm && (
                <div className="pt-2">
                  <button
                    onClick={() => setShowDisableConfirm(true)}
                    className="px-4 py-2 bg-rose-50 dark:bg-rose-950/30 hover:bg-rose-100 dark:hover:bg-rose-900/40 text-rose-600 dark:text-rose-400 border border-rose-200 dark:border-rose-800 rounded-xl text-xs font-medium transition cursor-pointer"
                  >
                    2FA Védelem Kikapcsolása
                  </button>
                </div>
              )}

              {/* Disable confirm form */}
              {totpEnabled && showDisableConfirm && (
                <form onSubmit={handleDisable2FA} className="p-4 bg-rose-50/50 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800/50 rounded-2xl space-y-3">
                  <div className="text-xs font-semibold text-rose-700 dark:text-rose-400">
                    A 2FA kikapcsolásához kérjük adja meg a jelszavát:
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="password"
                      required
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      placeholder="Jelenlegi jelszó..."
                      className="flex-1 py-1.5 px-3 bg-white dark:bg-slate-900 border border-rose-300 dark:border-rose-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-rose-500"
                    />
                    <button
                      type="submit"
                      disabled={twoFaLoading}
                      className="px-3 py-1.5 bg-rose-600 hover:bg-rose-500 text-white rounded-xl text-xs font-medium transition disabled:opacity-50 cursor-pointer"
                    >
                      Kikapcsolás Megerősítése
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDisableConfirm(false);
                        setDisablePassword('');
                      }}
                      className="px-2.5 py-1.5 text-xs text-slate-500 hover:text-slate-700"
                    >
                      Mégse
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
